// src/pages/Login.js
// Login page for FlowRCM production

import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts';
import FlowRCMLogo from '../components/FlowRCMLogo';
import { logFailedLogin } from '../services/auditService';

const Login = () => {
  const navigate = useNavigate();
  const { signIn, needsNewPassword, completeNewPassword } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Check for timeout parameter and show message
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('timeout') === 'true') {
      setError('Your session has expired due to inactivity. Please sign in again.');
      // Clean up URL
      window.history.replaceState({}, '', '/login');
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email || !password) {
      setError('Please enter both email and password');
      return;
    }

    try {
      setError('');
      setLoading(true);
      const result = await signIn(email, password);
      if (result?.needsNewPassword) {
        // Don't navigate - show new password form
        setLoading(false);
        return;
      }
      navigate('/'); // Redirect to dashboard on success
    } catch (error) {
      // HIPAA: Log failed login attempt
      await logFailedLogin(email);
      const msg = error.message || '';
      if (msg.includes('User does not exist') || msg.includes('user not found') || msg.includes('UserNotFoundException')) {
        setError('No account found with that email address. Please check your email or sign up for a new account.');
      } else if (msg.includes('Incorrect username or password') || msg.includes('NotAuthorizedException')) {
        setError('Incorrect email or password. Please try again.');
      } else if (msg.includes('User is not confirmed')) {
        setError('Your account has not been verified. Please check your email for a verification link.');
      } else if (msg.includes('already a signed in user')) {
        setError('Another account is currently signed in. Please sign out first before switching accounts.');
      } else {
        setError('Unable to sign in. Please check your credentials and try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleNewPassword = async (e) => {
    e.preventDefault();

    if (newPassword !== confirmNewPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    try {
      setError('');
      setLoading(true);
      await completeNewPassword(newPassword);
      navigate('/');
    } catch (error) {
      setError(error.message || 'Failed to set new password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-4">
          <div className="flex justify-center mb-4">
            <FlowRCMLogo variant="light" hideSubtitle={true} />
          </div>
          <h1 className="text-2xl font-bold text-slate-900">Welcome Back</h1>
          <p className="text-slate-600 mt-2">Sign in to your FlowRCM account</p>
        </div>

        {/* Login Card */}
        <div className="bg-white rounded-xl border border-slate-200 p-8" style={{boxShadow: '0 0 10px rgba(0,0,0,0.1)'}}>
          {needsNewPassword ? (
            /* New Password Form */
            <form onSubmit={handleNewPassword} className="space-y-6">
              <div className="text-center mb-4">
                <h2 className="text-lg font-semibold text-slate-900">Set New Password</h2>
                <p className="text-sm text-slate-600">Please set a new password for your account</p>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                  {error}
                </div>
              )}

              <div>
                <label htmlFor="newPassword" className="block text-sm font-semibold text-slate-700 mb-2">
                  New Password
                </label>
                <input
                  id="newPassword"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                  placeholder="••••••••"
                  disabled={loading}
                />
              </div>

              <div>
                <label htmlFor="confirmNewPassword" className="block text-sm font-semibold text-slate-700 mb-2">
                  Confirm New Password
                </label>
                <input
                  id="confirmNewPassword"
                  type="password"
                  value={confirmNewPassword}
                  onChange={(e) => setConfirmNewPassword(e.target.value)}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                  placeholder="••••••••"
                  disabled={loading}
                />
              </div>

              <p className="text-xs text-slate-500">
                Password must be at least 8 characters and include uppercase, lowercase, number, and special character.
              </p>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-teal-600 text-white py-3 rounded-lg font-semibold hover:bg-teal-700 disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Setting password...
                  </>
                ) : (
                  'Set Password & Continue'
                )}
              </button>
            </form>
          ) : (
            /* Regular Login Form */
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Error Message */}
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                  {error}
                </div>
              )}

              {/* Email Input */}
              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-slate-700 mb-2">
                  Email Address
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                  placeholder="you@dentalclinic.com"
                  disabled={loading}
                />
              </div>

              {/* Password Input */}
              <div>
                <label htmlFor="password" className="block text-sm font-semibold text-slate-700 mb-2">
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                  placeholder="••••••••"
                  disabled={loading}
                />
              </div>

              {/* Forgot Password */}
              <div className="flex items-center justify-between">
                <Link to="/forgot-password" className="text-sm text-teal-600 hover:text-teal-700 font-medium">
                  Forgot password?
                </Link>
              </div>

              {/* Sign In Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-teal-600 text-white py-3 rounded-lg font-semibold hover:bg-teal-700 disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Signing in...
                  </>
                ) : (
                  'Sign In'
                )}
              </button>
            </form>
          )}

          {/* Sign Up Link */}
          {!needsNewPassword && (
            <div className="mt-6 text-center">
              <p className="text-slate-600 text-sm">
                Don't have an account?{' '}
                <Link to="/signup" className="text-teal-600 hover:text-teal-700 font-semibold">
                  Create Account
                </Link>
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="mt-4 text-center text-sm text-slate-500">
          <p>© 2026 FlowRCM. All rights reserved.</p>
          <div className="mt-2 space-x-4">
            <Link to="/privacy" className="hover:text-slate-700">Privacy Policy</Link>
            <span>•</span>
            <Link to="/terms" className="hover:text-slate-700">Terms of Service</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;